import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from 'src/hooks/redux/useAppDispatch'
import { AppThunk, RootState } from 'src/store/store'
import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import {
  ResetCallTime,
  ResetCameraStatus,
  ResetEmptyComponentStatus,
  setCallTimeCounter,
  setEmptyComponentStatus,
} from 'src/store/Reducer/page/cameraPageSlice'
import CameraTemplate from 'src/components/atomic/template/Service/Camera'
import useCameraEventBus from 'src/hooks/useEventBus/useCameraEventBus'
import { SvgType } from 'src/types/types'
import { COLOR } from 'src/constants/colors'
import { Cmdtypes } from 'src/constants/cmdtypes'
import { ColorHandler } from 'src/utils/style.utils'
import { SocketSender } from 'src/api/socket'
import { useBeforeUnload } from 'react-router-dom'
import dayjs from 'dayjs'
import { setModalItems } from 'src/store/Reducer/common/modalSlice'
import { useTranslation } from 'react-i18next'

export type CameraDataPropsType = {
  CameraStartHandler: () => void
  CameraActiveItems: CameraActiveItemsType[]
}
export type CameraActiveItemsType = {
  svgName: SvgType
  bgColor?: string
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  svgColor?: string
  disabled?: boolean
  className?: string
}

const CameraPage = () => {
  const publisherRef = useRef<any>(null)
  const SessionRef = useRef<any>(null)
  const dispatch = useAppDispatch()
  const deviceAuth = useAppSelector((state: RootState) => state.deviceAuth)
  const { Connection } = useAppSelector((state: RootState) => state.connection)
  const [CamOpen, setCamOpen] = useState(true)
  const [MicOpen, setMicOpen] = useState(true)
  const { CameraStatus } = useAppSelector((state: RootState) => state.CameraPage)
  const { t } = useTranslation()

  const [buttonDisabledFlag, setButtonDisabledFlag] = useState({
    cam: true,
    mic: true,
    rotate: true,
  })
  const { mic, cam, rotate } = buttonDisabledFlag

  const CameraStartHandler = () => {
    //1초 지연 사유 : Mars-Issue #312 3017해제 받기 전에 영상통화 요청 이슈
    setTimeout(() => {
      switch (true) {
        case !Connection:
          dispatch(setEmptyComponentStatus('CameraOffline'))
          break
        case !deviceAuth.Camera:
          dispatch(setEmptyComponentStatus('CameraNoAuth'))
          break
        case CameraStatus === 'default':
          dispatch(SocketSender(Cmdtypes.CAMERA_START))
          break
      }
    }, 1000)
  }

  const CameraStopHandler = (): AppThunk => {
    return async (_dispatch, getState) => {
      const { CameraStatus } = getState().CameraPage
      CameraStatus !== 'default' && dispatch(SocketSender(Cmdtypes.CAMERA_STOP))
      SessionRef.current?.disconnect()
      dispatch(ResetEmptyComponentStatus())
      dispatch(ResetCameraStatus())
      dispatch(ResetCallTime())
      setMicOpen(true)
      setCamOpen(true)
      if (CameraStatus === 'active' && Connection) {
        dispatch(
          setModalItems({
            type: 'alert',
            text: t('MODAL.CAMERA_END', {
              defaultValue: 'Ending video Call',
            }) as string,
          }),
        )
      }
    }
  }

  const CameraActiveItems: CameraActiveItemsType[] = [
    {
      svgName: 'camera_cam',
      disabled: !cam,
      onClick: () => PublisherDeviceHandler('cam'),
      bgColor: cam ? ColorHandler(!CamOpen, 'white_black_opacity') : COLOR.blackOpacity07,
      svgColor: cam ? ColorHandler(CamOpen, 'white_black_opacity') : COLOR.CameraDisabledSvg,
    },
    {
      svgName: 'camera_mic',
      disabled: !mic,
      onClick: () => PublisherDeviceHandler('mic'),
      bgColor: mic ? ColorHandler(!MicOpen, 'white_black_opacity') : COLOR.blackOpacity07,
      svgColor: mic ? ColorHandler(MicOpen, 'white_black_opacity') : COLOR.CameraDisabledSvg,
    },
    {
      svgName: 'camera_cam_rotate',
      disabled: !rotate,
      onClick: () => handleCamRotate(),
      bgColor: COLOR.blackOpacity07,
      svgColor: rotate ? COLOR.white : COLOR.CameraDisabledSvg,
    },
    {
      svgName: 'camera_end',
      disabled: false,
      onClick: () => dispatch(CameraStopHandler()),
      bgColor: COLOR.red80,
      className: 'camera_connection_end',
    },
  ]

  const handleCamRotate = () => {
    dispatch(SocketSender(Cmdtypes.CAMERA_CAM_ROTATE))
    setButtonDisabledFlag({
      ...buttonDisabledFlag,
      rotate: false,
    })
  }

  const PublisherDeviceHandler = (option: string) => {
    switch (true) {
      case !publisherRef.current.accessAllowed:
        break
      case option === 'cam':
        if (publisherRef.current.stream.hasVideo) {
          publisherRef.current.publishVideo(false)
          setCamOpen(false)
        } else {
          publisherRef.current.publishVideo(true)
          setCamOpen(true)
        }
        setButtonDisabledFlag({
          ...buttonDisabledFlag,
          cam: false,
        })
        break
      case option === 'mic':
        if (publisherRef.current.stream.hasAudio) {
          publisherRef.current.publishAudio(false)
          setMicOpen(false)
        } else {
          publisherRef.current.publishAudio(true)
          setMicOpen(true)
        }
        setButtonDisabledFlag({
          ...buttonDisabledFlag,
          mic: false,
        })
        break
      default:
        break
    }
  }

  useCameraEventBus(SessionRef, publisherRef)

  useEffect(() => {
    !Connection && dispatch(CameraStopHandler())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Connection])

  useEffect(() => {
    dispatch(ResetEmptyComponentStatus())
    dispatch(ResetCameraStatus())

    return () => {
      dispatch(CameraStopHandler())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useBeforeUnload(
    useCallback(
      (e) => {
        CameraStatus !== 'default' && dispatch(SocketSender(Cmdtypes.CAMERA_STOP))
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [CameraStatus],
    ),
  )

  useEffect(() => {
    if (!mic || !cam || !rotate) {
      setTimeout(() => {
        setButtonDisabledFlag({ cam: true, mic: true, rotate: true })
      }, 5000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonDisabledFlag])

  //스톱워치 , Component Name: StopWatch
  useEffect(() => {
    let interval: NodeJS.Timer
    if (CameraStatus === 'active') {
      const currentTime = dayjs().unix()
      interval = setInterval(() => {
        if (dayjs().unix() - currentTime === 1800) {
          dispatch(CameraStopHandler())
        } else dispatch(setCallTimeCounter(dayjs().unix() - currentTime))
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CameraStatus])

  const data: CameraDataPropsType = {
    CameraActiveItems,
    CameraStartHandler,
  }
  return <CameraTemplate data={data} />
}

export default CameraPage
